import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CreditCard, Gauge, CheckCircle2, Lock, Sparkles, Crown, ArrowRight, RefreshCw,
  FileText, Receipt, Package, Send, MessageSquare, Clock, Check, X, Building2,
  LayoutDashboard, Wallet, Code2, Users, Cloud, ShieldCheck, Zap, Headphones,
  Phone, Info, ChevronRight, ShoppingCart, TrendingUp, HardDrive,
  Download, HelpCircle, CalendarDays, Sprout,
} from "lucide-react";
import { useToast } from "../Context/ToastContext";
import {
  getCurrentSubscription, getUsage, getPlans, subErr,
  getInvoices, getPayments, getAddonCatalog, getMyAddons,
  createChangeRequest, getMyChangeRequests,
  createCheckout, openRazorpayCheckout, upgradePlan, renewSubscription,
} from "./subscriptionApi";
import { useFeatures } from "./FeaturesContext";
import "../../styles/subscription.css";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "plans", label: "Plans & Upgrade", icon: Crown },
  { id: "billing", label: "Billing & Payments", icon: Wallet },
  { id: "addons", label: "Add-ons", icon: Package },
];

const MONTHLY_PRICE = 50;
const ANNUAL_DISCOUNT = 0.17;
const ANNUAL_PRICE = Math.round(MONTHLY_PRICE * (1 - ANNUAL_DISCOUNT));
const MRP = 99;
const DISCOUNT_PCT = Math.round(((MRP - MONTHLY_PRICE) / MRP) * 100);
const MIN_MONTHLY = 1999;
const MIN_EMPLOYEES = 40;

const PLAN_FEATURE_ROWS = [
  { key: "payroll", label: "Payroll & payslips", free: true, premium: true, custom: true },
  { key: "attendance", label: "Attendance", free: true, premium: true, custom: true },
  { key: "leave", label: "Leave management", free: true, premium: true, custom: true },
  { key: "workflow", label: "Approval workflows", free: false, premium: true, custom: true },
  { key: "roster", label: "Shift & roster", free: false, premium: true, custom: true },
  { key: "recruitment", label: "Recruitment", free: false, premium: true, custom: true },
  { key: "performance", label: "Performance", free: false, premium: true, custom: true },
  { key: "assets", label: "Assets", free: false, premium: true, custom: true },
  { key: "api", label: "API access", free: false, premium: false, custom: true },
  { key: "audit", label: "Audit logs", free: false, premium: true, custom: true },
  { key: "branding", label: "Organization branding", free: false, premium: false, custom: true },
  { key: "custom_wf", label: "Custom workflows", free: false, premium: false, custom: true },
  { key: "support", label: "Dedicated support & SLA", free: false, premium: false, custom: true },
];

const FEATURE_ROWS = [
  { key: "payroll", label: "Payroll & payslips", fn: () => true },
  { key: "attendance", label: "Attendance", fn: () => true },
  { key: "leave", label: "Leave management", feat: "leave.manage" },
  { key: "workflow", label: "Approval workflows", feat: "workflow.approvals" },
  { key: "roster", label: "Shift & roster", feat: "roster.manage" },
  { key: "recruitment", label: "Recruitment", feat: "recruitment.manage" },
  { key: "api", label: "API access", feat: "api.access" },
];

const OV_FEATURE_META = {
  employees: { icon: Users, desc: "Add and manage employees" },
  storage: { icon: HardDrive, desc: "Cloud file storage" },
  payroll: { icon: FileText, desc: "Run payroll & generate payslips" },
  attendance: { icon: Clock, desc: "Track daily attendance" },
  leave: { icon: CalendarDays, desc: "Manage leaves & balances" },
  workflow: { icon: CheckCircle2, desc: "Multi-level approval flows" },
  roster: { icon: RefreshCw, desc: "Manage shifts & rosters" },
  recruitment: { icon: Building2, desc: "Hiring & onboarding" },
  api: { icon: Code2, desc: "Developer API access" },
};
const OV_USAGE_ICONS = { employees: Users, branches: Building2, storage: HardDrive, storage_gb: HardDrive, payroll_runs: FileText };
const OV_ADDON_MAP = { "api.access": "ADDON_API" };
const OV_ADDON_DISPLAY = {
  EMP_PACK_50: { icon: Users, desc: "Add 50 more employees to your plan" },
  EMP_PACK_200: { icon: Users, desc: "Add 200 more employees to your plan" },
  STORAGE_50: { icon: HardDrive, desc: "Add 50 GB of additional cloud storage" },
  ADDON_API: { icon: Code2, desc: "Enable developer API access for integrations" },
};

const fmtLimit = (v) => (v === -1 || v == null ? "Unlimited" : v);
const titleize = (k) => String(k || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function hasFeature(plan, code) {
  if (!plan?.features) return false;
  if (Array.isArray(plan.features)) return plan.features.includes(code);
  return Boolean(plan.features[code]);
}

export default function SubscriptionAndPlansPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const { refresh: refreshFeatures } = useFeatures();

  const [current, setCurrent] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [addonCatalog, setAddonCatalog] = useState([]);
  const [myAddons, setMyAddons] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ request_type: "upgrade", target_plan: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [selectedEmpPack, setSelectedEmpPack] = useState("EMP_PACK_50");
  const [featModalOpen, setFeatModalOpen] = useState(false);
  const [planBilling, setPlanBilling] = useState("monthly");

  const setTab = (id) => setSearchParams({ tab: id }, { replace: true });

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const [c, u, p, inv, pay, cat, mine, rq] = await Promise.all([
        getCurrentSubscription().catch(() => null),
        getUsage().catch(() => null),
        getPlans().catch(() => []),
        getInvoices().catch(() => []),
        getPayments().catch(() => []),
        getAddonCatalog().catch(() => []),
        getMyAddons().catch(() => []),
        getMyChangeRequests().catch(() => []),
      ]);
      setCurrent(c);
      setUsage(u);
      setPlans((p || []).filter((x) => x.code !== "custom"));
      setInvoices(inv || []);
      setPayments(pay || []);
      setAddonCatalog(cat || []);
      setMyAddons(mine || []);
      setReqs(rq || []);
    } catch (e) {
      setLoadError(subErr(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const currentCode = current?.plan?.code;
  const orderedPlans = useMemo(
    () => [...plans].sort((a, b) => (a.tier || 0) - (b.tier || 0)),
    [plans]
  );
  const enabledFeatures = useMemo(() => Object.entries(current?.features || {}), [current]);
  const enabledCount = enabledFeatures.filter(([, v]) => v).length;
  const currentPlan = plans.find(p => p.code === currentCode);
  const planPrice = currentPlan?.pricing?.monthly?.amount;
  const includedFeats = FEATURE_ROWS.filter(row => row.fn ? Boolean(row.fn(currentPlan || {})) : row.feat ? Boolean(current?.features?.[row.feat]) : false);
  const upgradeFeats = FEATURE_ROWS.filter(row => {
    if (row.fn) { const v = row.fn(currentPlan || {}); return v === false || v == null; }
    return row.feat ? !current?.features?.[row.feat] && !OV_ADDON_MAP[row.feat] : false;
  });
  const pendingInvoices = invoices.filter((i) => i.status === "pending" && Number(i.total) > 0);
  const addonPrice = (code, fallback) => { const a = addonCatalog.find(x => x.code === code); return a ? Number(a.price) : fallback; };
  const otherAddons = addonCatalog.filter(a => !["ADDON_API", "EMP_PACK_50", "EMP_PACK_200", "STORAGE_50"].includes(a.code));

  function scrollTop() {
    const el = document.querySelector(".content-area") || document.scrollingElement || document.documentElement;
    el.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openRequest(type, plan = "") {
    setReqForm({ request_type: type, target_plan: plan, message: "" });
    setReqOpen(true);
    scrollTop();
  }

  async function payInvoice(invoiceId) {
    try {
      setBusy(true);
      const checkout = await createCheckout(invoiceId);
      await openRazorpayCheckout(checkout, async () => {
        toast.success("Payment recorded. Your subscription is active.");
        await load();
        refreshFeatures?.();
        navigate("/payment/success");
      });
    } catch (e) {
      if (e?.message !== "Payment cancelled") {
        toast.error(subErr(e));
        navigate("/payment/failed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleUpgrade(planCode) {
    if (planCode === "enterprise") {
      openRequest("contact_sales", "enterprise");
      return;
    }
    try {
      setBusy(true);
      const result = await upgradePlan(planCode);
      const invoice = result?.invoice;
      if (invoice?.invoice_id && Number(invoice.total) > 0) {
        await payInvoice(invoice.invoice_id);
      } else {
        toast.success("Plan updated.");
        await load();
        refreshFeatures?.();
      }
    } catch (e) {
      toast.error(subErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleRenew() {
    try {
      setBusy(true);
      const result = await renewSubscription();
      const invoice = result?.invoice;
      if (invoice?.invoice_id && Number(invoice.total) > 0) {
        await payInvoice(invoice.invoice_id);
      } else {
        toast.success("Subscription renewed.");
        await load();
        refreshFeatures?.();
      }
    } catch (e) {
      toast.error(subErr(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRequest() {
    try {
      setBusy(true);
      await createChangeRequest(reqForm);
      toast.success("Request sent to billing team.");
      setReqOpen(false);
      await load();
    } catch (e) {
      toast.error(subErr(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="sub-page sub-page-shell">
        <div className="sub-card sub-loading">Loading subscription &amp; plans…</div>
      </div>
    );
  }

  return (
    <div className="sub-page sub-page-shell">
      <header className="sub-hero">
        <div className="sub-hero-left">
          <span className="sub-hero-icon"><CreditCard size={22} /></span>
          <div>
            <div className="sub-eyebrow">Billing</div>
            <h1 className="sub-title">Subscription &amp; Plans</h1>
            <p className="sub-sub">
              Manage your plan, compare upgrades, pay invoices via Razorpay, and track usage — all in one place.
            </p>
          </div>
        </div>
        <div className="sub-current-actions">
          <button type="button" className="sub-btn sub-btn-ghost" onClick={load} disabled={busy}>
            <RefreshCw size={14} /> Refresh
          </button>
          {pendingInvoices.length > 0 ? (
            <button type="button" className="sub-btn sub-btn-primary" disabled={busy} onClick={() => payInvoice(pendingInvoices[0].invoice_id)}>
              Pay ₹{Number(pendingInvoices[0].total).toLocaleString("en-IN")}
            </button>
          ) : (
            <button type="button" className="sub-btn sub-btn-primary" onClick={handleRenew} disabled={busy}>
              <Send size={14} /> Renew
            </button>
          )}
        </div>
      </header>

      {loadError ? (
        <div className="sub-card sub-banner-danger" style={{ borderRadius: 12, marginBottom: 0 }}>
          Could not load all billing data: {loadError}. Try refresh or contact support.
        </div>
      ) : null}

      <nav className="sub-tabs" aria-label="Subscription sections">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`sub-tab${tab === id ? " active" : ""}`}
            onClick={() => setTab(id)}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </nav>

      {/* ── OVERVIEW ── */}
      {tab === "overview" ? (
        <>
          <section className="sub-ov-summary">
            <div className="sub-ov-summary-top">
              <div className="sub-ov-summary-left">
                <div className="sub-ov-summary-icon"><Crown size={24} /></div>
                <div>
                  <div className="sub-ov-eyebrow">Your current plan</div>
                  <div className="sub-ov-plan-name">
                    {current?.plan?.name || "No plan"} Plan
                    <span className={`sub-ov-status sub-ov-status-${current?.status}`}>{current?.status || "—"}</span>
                  </div>
                  <div className="sub-ov-plan-meta">
                    {planPrice ? (
                      <span className="sub-ov-price">
                        ₹{Number(planPrice).toLocaleString("en-IN")}<span className="sub-ov-price-per">/month</span>
                      </span>
                    ) : null}
                    {current?.billing_cycle ? <span className="sub-ov-meta-chip">{current.billing_cycle}</span> : null}
                    {current?.next_billing_date ? (
                      <span className="sub-ov-meta-item"><RefreshCw size={12} /> Renews {current.next_billing_date}</span>
                    ) : null}
                    {current?.trial_ends_at ? (
                      <span className="sub-ov-meta-item sub-ov-meta-trial"><Clock size={12} /> Trial ends {current.trial_ends_at.slice(0, 10)}</span>
                    ) : null}
                    <span className="sub-ov-meta-item sub-ov-meta-razorpay"><ShieldCheck size={12} /> Powered by Razorpay</span>
                  </div>
                </div>
              </div>
              <div className="sub-ov-summary-right">
                <div className="sub-ov-feat-highlight">
                  <div className="sub-ov-feat-circle">
                    <span className="sub-ov-feat-num">{enabledCount}</span>
                    <span className="sub-ov-feat-of">of {enabledFeatures.length}</span>
                  </div>
                  <span className="sub-ov-feat-label">features included</span>
                </div>
                <button type="button" className="sub-ov-upgrade-btn" onClick={() => setTab("plans")}>
                  View plans &amp; upgrade <ArrowRight size={14} />
                </button>
              </div>
            </div>
            {current?.is_grandfathered ? (
              <div className="sub-ov-grandfathered"><Info size={14} /> Grandfathered pricing — your rate is locked in</div>
            ) : null}
          </section>

          <div className="sub-ov-two-col">
            <section className="sub-card sub-ov-usage-section">
              <div className="sub-ov-sec-header">
                <h2 className="sub-ov-sec-title"><Gauge size={18} /> Your plan usage</h2>
                <button type="button" className="sub-ov-sec-link" onClick={() => setTab("billing")}>View all usage <ArrowRight size={12} /></button>
              </div>
              <div className="sub-ov-usage-rows">
                {(usage?.usage || []).map((u, idx) => {
                  const UIcon = OV_USAGE_ICONS[u.metric] || Gauge;
                  const pct = u.unlimited ? 0 : Math.min(100, u.percent || 0);
                  const colorClass = u.exceeded ? "over" : u.percent >= 80 ? "warn" : u.unlimited ? "unlimited" : "";
                  return (
                    <div className={`sub-ov-urow${idx > 0 ? " sub-ov-urow-sep" : ""}`} key={u.metric}>
                      <div className={`sub-ov-urow-icon ${colorClass}`}><UIcon size={14} /></div>
                      <div className="sub-ov-urow-info">
                        <div className="sub-ov-urow-top">
                          <span className="sub-ov-urow-name">{titleize(u.metric)}</span>
                          <span className="sub-ov-urow-val">{u.used} / {fmtLimit(u.limit)}</span>
                        </div>
                        <div className="sub-ov-urow-bar-wrap">
                          <div className="sub-ov-urow-track">
                            <div className={`sub-ov-urow-fill ${colorClass}`} style={{ width: `${u.unlimited ? 100 : Math.max(3, pct)}%` }} />
                          </div>
                          <span className={`sub-ov-urow-pct ${colorClass}`}>
                            {u.unlimited ? "∞" : u.exceeded ? `${Math.max(0, u.remaining)} remaining` : `${Math.round(pct)}%`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!usage?.usage || usage.usage.length === 0) ? (
                  <div className="sub-ov-empty"><Gauge size={28} /><span>No usage metrics available yet.</span></div>
                ) : null}
              </div>
            </section>

            <section className="sub-card sub-ov-included-section">
              <div className="sub-ov-sec-header">
                <h2 className="sub-ov-sec-title"><Sparkles size={18} /> What's included in your plan</h2>
                <button type="button" className="sub-ov-sec-link" onClick={() => { setFeatModalOpen(true); scrollTop(); }}>View all features <ArrowRight size={12} /></button>
              </div>
              <div className="sub-ov-included-grid">
                {FEATURE_ROWS.map((row) => {
                  const meta = OV_FEATURE_META[row.key] || {};
                  const FIcon = meta.icon || Sparkles;
                  let status = "included";
                  let valueText = meta.desc || row.label;
                  if (row.fn) {
                    const val = row.fn(currentPlan || {});
                    if (val === false || val == null) { status = "not-included"; valueText = "Not in your plan"; }
                    else if (typeof val === "string" || typeof val === "number") { valueText = String(val); }
                  } else if (row.feat) {
                    const enabled = current?.features?.[row.feat];
                    if (!enabled) {
                      status = OV_ADDON_MAP[row.feat] ? "addon" : "not-included";
                      valueText = status === "addon" ? "Available as add-on" : "Not in your plan";
                    }
                  }
                  return (
                    <div className={`sub-ov-feat-item sub-ov-feat-${status}`} key={row.key}>
                      <div className={`sub-ov-feat-icon sub-ov-feat-icon-${status}`}>
                        <FIcon size={16} />
                      </div>
                      <div className="sub-ov-feat-body">
                        <div className="sub-ov-feat-name">{row.label}</div>
                        <div className="sub-ov-feat-desc">{valueText}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="sub-ov-legend">
                <span className="sub-ov-legend-item"><span className="sub-ov-legend-dot sub-ov-legend-ok"><Check size={10} /></span> Included</span>
                <span className="sub-ov-legend-item"><span className="sub-ov-legend-dot sub-ov-legend-addon"><ShoppingCart size={10} /></span> Add-on available</span>
                <span className="sub-ov-legend-item"><span className="sub-ov-legend-dot sub-ov-legend-no"><Lock size={10} /></span> Not included</span>
              </div>
            </section>
          </div>

          <div className="sub-ov-three-col">
            <section className="sub-card sub-ov-glance">
              <h3 className="sub-ov-card-title"><Info size={16} /> At a glance</h3>
              <div className="sub-ov-glance-rows">
                <div className="sub-ov-glance-row">
                  <span className="sub-ov-glance-label">Plan</span>
                  <span className="sub-ov-glance-value">{current?.plan?.name || "—"}</span>
                </div>
                <div className="sub-ov-glance-row">
                  <span className="sub-ov-glance-label">Status</span>
                  <strong className={`sub-ov-glance-status sub-ov-glance-st-${current?.status}`}>{current?.status || "—"}</strong>
                </div>
                <div className="sub-ov-glance-row">
                  <span className="sub-ov-glance-label">Billing cycle</span>
                  <span className="sub-ov-glance-value">{current?.billing_cycle || "—"}</span>
                </div>
                {current?.next_billing_date ? (
                  <div className="sub-ov-glance-row">
                    <span className="sub-ov-glance-label">Next renewal</span>
                    <span className="sub-ov-glance-value">{current.next_billing_date}</span>
                  </div>
                ) : null}
                {current?.trial_ends_at ? (
                  <div className="sub-ov-glance-row">
                    <span className="sub-ov-glance-label">Trial ends</span>
                    <span className="sub-ov-glance-value">{current.trial_ends_at.slice(0, 10)}</span>
                  </div>
                ) : null}
                <div className="sub-ov-glance-row">
                  <span className="sub-ov-glance-label">Features</span>
                  <span className="sub-ov-glance-value">{enabledCount} enabled</span>
                </div>
              </div>
              {current?.status === "trial" ? (
                <div className="sub-ov-trial-badge"><Zap size={12} /> Free trial</div>
              ) : null}
            </section>

            <section className="sub-card sub-ov-recent-inv">
              <h3 className="sub-ov-card-title"><Receipt size={16} /> Recent invoice</h3>
              {invoices.length > 0 ? (
                <div className="sub-ov-inv-details">
                  <div className="sub-ov-inv-row">
                    <span className="sub-ov-inv-label">Invoice</span>
                    <span className="sub-ov-inv-id">#{invoices[0].invoice_id?.slice(-6) || "—"}</span>
                  </div>
                  <div className="sub-ov-inv-row">
                    <span className="sub-ov-inv-label">Amount</span>
                    <span className="sub-ov-inv-amount">₹{Number(invoices[0].total || 0).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="sub-ov-inv-row">
                    <span className="sub-ov-inv-label">Status</span>
                    <span className={`sub-inv-status s-${invoices[0].status}`}>{invoices[0].status}</span>
                  </div>
                  {invoices[0].status === "pending" && Number(invoices[0].total) > 0 ? (
                    <button type="button" className="sub-ov-pay-btn" disabled={busy} onClick={() => payInvoice(invoices[0].invoice_id)}>
                      <CreditCard size={14} /> Pay now
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="sub-ov-no-inv"><Receipt size={32} /><span>No invoices yet</span></div>
              )}
              <button type="button" className="sub-ov-link-btn" onClick={() => setTab("billing")}>
                View all invoices <ArrowRight size={13} />
              </button>
            </section>

            <section className="sub-card sub-ov-need">
              <h3 className="sub-ov-card-title"><HelpCircle size={16} /> Need something?</h3>
              <div className="sub-ov-need-list">
                <button type="button" className="sub-ov-need-opt" onClick={() => openRequest("upgrade")}>
                  <span className="sub-ov-need-ic"><TrendingUp size={14} /></span>
                  <span className="sub-ov-need-text">Upgrade plan</span>
                  <ChevronRight size={14} className="sub-ov-need-arrow" />
                </button>
                <button type="button" className="sub-ov-need-opt" onClick={() => openRequest("downgrade")}>
                  <span className="sub-ov-need-ic"><ArrowRight size={14} style={{ transform: "rotate(90deg)" }} /></span>
                  <span className="sub-ov-need-text">Downgrade</span>
                  <ChevronRight size={14} className="sub-ov-need-arrow" />
                </button>
                <button type="button" className="sub-ov-need-opt" onClick={() => openRequest("cancel")}>
                  <span className="sub-ov-need-ic"><X size={14} /></span>
                  <span className="sub-ov-need-text">Cancel plan</span>
                  <ChevronRight size={14} className="sub-ov-need-arrow" />
                </button>
                <button type="button" className="sub-ov-need-opt" onClick={() => openRequest("contact_sales")}>
                  <span className="sub-ov-need-ic"><Headphones size={14} /></span>
                  <span className="sub-ov-need-text">Contact support</span>
                  <ChevronRight size={14} className="sub-ov-need-arrow" />
                </button>
              </div>
            </section>
          </div>

          <div className="sub-ov-footer">
            <div className="sub-ov-footer-left"><ShieldCheck size={14} /> Secure &amp; trusted payments</div>
            <div className="sub-ov-footer-right">Powered by <span className="sub-ov-footer-brand">Razorpay</span></div>
          </div>
        </>
      ) : null}

      {/* ── PLANS ── */}
      {tab === "plans" ? (() => {
        const isAnnual = planBilling === "annual";
        const price = isAnnual ? ANNUAL_PRICE : MONTHLY_PRICE;
        const annualMinMonthly = Math.round(ANNUAL_PRICE * MIN_EMPLOYEES);
        return (
        <>
          <section className="sub-card">
            <h2 className="sub-section-title"><Crown size={18} /> Choose your plan</h2>
            <p className="sub-muted-sm" style={{ marginBottom: "1rem" }}>
              Start free. Upgrade when you need more. No credit card required.
            </p>

            <div className="bxp-toggle-row" style={{ justifyContent: "center", marginBottom: "1.5rem" }}>
              <div className="bxp-toggle">
                <button type="button" className={`bxp-toggle-btn ${planBilling === "monthly" ? "active" : ""}`} onClick={() => setPlanBilling("monthly")}>Monthly</button>
                <button type="button" className={`bxp-toggle-btn ${planBilling === "annual" ? "active" : ""}`} onClick={() => setPlanBilling("annual")}>Annual</button>
              </div>
              <span className="bxp-save-tag">
                <svg width="28" height="18" viewBox="0 0 28 18" fill="none" className="bxp-save-arrow">
                  <path d="M26 17C20 10 14 4 2 2" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                  <path d="M7 1L2 2L3 7" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
                Save up to 17%
              </span>
            </div>

            <div className="bxp-grid">
              {/* Free */}
              <article className={`bxp-card bxp-card--free ${currentCode === "free" ? "current-plan" : ""}`}>
                {currentCode === "free" && <span className="sub-plan-current">Current</span>}
                <div className="bxp-card-head">
                  <span className="bxp-card-icon bxp-card-icon--green"><Sprout size={22} /></span>
                  <h2 className="bxp-card-name">Free</h2>
                  <p className="bxp-card-desc">For small teams to get started</p>
                </div>
                <div className="bxp-price-block">
                  <span className="bxp-price">₹0</span>
                  <span className="bxp-price-per">/ month</span>
                </div>
                <span className="bxp-emp-badge">Up to 10 employees</span>
                <ul className="bxp-features">
                  <li><Check size={15} className="bxp-check" /> Employee management</li>
                  <li><Check size={15} className="bxp-check" /> Basic attendance</li>
                  <li><Check size={15} className="bxp-check" /> Basic leave management</li>
                  <li><Check size={15} className="bxp-check" /> Employee self-service</li>
                  <li><Check size={15} className="bxp-check" /> Basic reports</li>
                </ul>
                <button type="button" className={`bxp-cta bxp-cta--outline ${currentCode === "free" ? "bxp-cta--disabled" : ""}`} disabled={currentCode === "free" || busy} onClick={() => handleUpgrade("free")}>
                  {currentCode === "free" ? "Current plan" : "Get Started Free"}
                </button>
              </article>

              {/* Premium */}
              <article className={`bxp-card bxp-card--premium ${currentCode === "premium" ? "current-plan" : ""}`}>
                <span className="bxp-badge">Best Value</span>
                {currentCode === "premium" && <span className="sub-plan-current">Current</span>}
                <div className="bxp-card-head">
                  <span className="bxp-card-icon bxp-card-icon--blue"><Users size={22} /></span>
                  <h2 className="bxp-card-name">Premium</h2>
                  <p className="bxp-card-desc">Complete HRMS & Payroll</p>
                </div>
                <div className="bxp-price-block">
                  <span className="bxp-price-mrp">₹{MRP}</span>
                  <span className="bxp-price">₹{price}</span>
                  <span className="bxp-price-per">/ employee / month</span>
                  <span className="bxp-discount-tag">{DISCOUNT_PCT}% OFF</span>
                </div>
                <div className="bxp-min-price">
                  {isAnnual ? (
                    <>
                      <p>₹{annualMinMonthly.toLocaleString("en-IN")} / month for up to {MIN_EMPLOYEES} employees</p>
                      <p>₹{price} for each additional employee / month</p>
                    </>
                  ) : (
                    <>
                      <p>₹{MIN_MONTHLY.toLocaleString("en-IN")} / month for up to {MIN_EMPLOYEES} employees</p>
                      <p>₹{MONTHLY_PRICE} for each additional employee / month</p>
                    </>
                  )}
                </div>
                <ul className="bxp-features">
                  <li><Check size={15} className="bxp-check" /> All Brixigo modules</li>
                  <li><Check size={15} className="bxp-check" /> Payroll & statutory compliance</li>
                  <li><Check size={15} className="bxp-check" /> Attendance & leave management</li>
                  <li><Check size={15} className="bxp-check" /> Reports & analytics</li>
                  <li><Check size={15} className="bxp-check" /> Role-based access (IAM)</li>
                  <li><Check size={15} className="bxp-check" /> Employee self-service</li>
                  <li><Check size={15} className="bxp-check" /> Ongoing product updates</li>
                </ul>
                <button type="button" className={`bxp-cta bxp-cta--solid ${currentCode === "premium" ? "bxp-cta--disabled" : ""}`} disabled={currentCode === "premium" || busy} onClick={() => handleUpgrade("premium")}>
                  {currentCode === "premium" ? "Current plan" : <>Start Premium <ArrowRight size={14} /></>}
                </button>
              </article>

              {/* Customized */}
              <article className={`bxp-card bxp-card--custom ${currentCode === "enterprise" ? "current-plan" : ""}`}>
                {currentCode === "enterprise" && <span className="sub-plan-current">Current</span>}
                <div className="bxp-card-head">
                  <span className="bxp-card-icon bxp-card-icon--orange"><Building2 size={22} /></span>
                  <h2 className="bxp-card-name">Customized</h2>
                  <p className="bxp-card-desc">For growing & enterprise organizations</p>
                </div>
                <div className="bxp-price-block">
                  <span className="bxp-price bxp-price--dark">Contact Sales</span>
                </div>
                <p className="bxp-custom-sub">Tailored to your organization's needs</p>
                <ul className="bxp-features">
                  <li><Check size={15} className="bxp-check bxp-check--orange" /> All Premium features</li>
                  <li><Check size={15} className="bxp-check bxp-check--orange" /> Custom workflows</li>
                  <li><Check size={15} className="bxp-check bxp-check--orange" /> Advanced reporting</li>
                  <li><Check size={15} className="bxp-check bxp-check--orange" /> Integrations (API, third-party)</li>
                  <li><Check size={15} className="bxp-check bxp-check--orange" /> Multiple locations / departments</li>
                  <li><Check size={15} className="bxp-check bxp-check--orange" /> Dedicated support & SLA</li>
                </ul>
                <button type="button" className="bxp-cta bxp-cta--outline bxp-cta--orange" disabled={busy} onClick={() => handleUpgrade("enterprise")}>
                  Contact Sales
                </button>
              </article>
            </div>
          </section>

          <section className="sub-card sub-comparison">
            <h2 className="sub-section-title"><Building2 size={18} /> Feature comparison</h2>
            <div className="sub-table-wrap">
              <table className="sub-table sub-comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free</th>
                    <th>Premium</th>
                    <th>Customized</th>
                  </tr>
                </thead>
                <tbody>
                  {PLAN_FEATURE_ROWS.map((row) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="center">{row.free ? <Check size={16} className="ok" /> : <X size={16} className="no" />}</td>
                      <td className="center">{row.premium ? <Check size={16} className="ok" /> : <X size={16} className="no" />}</td>
                      <td className="center">{row.custom ? <Check size={16} className="ok" /> : <X size={16} className="no" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
        );
      })() : null}

      {/* ── BILLING ── */}
      {tab === "billing" ? (
        <>
          {/* Billing Summary */}
          <section className="sub-bill-summary">
            <h2 className="sub-bill-summary-heading">Billing summary</h2>
            <div className="sub-bill-summary-row">
              <div className="sub-bill-col">
                <div className="sub-bill-label">Outstanding amount</div>
                <div className="sub-bill-amount">₹{pendingInvoices.length > 0 ? Number(pendingInvoices[0].total).toLocaleString("en-IN") : "0"}</div>
                {pendingInvoices.length > 0 && <span className="sub-bill-badge warn">Payment pending</span>}
                {pendingInvoices.length > 0 && pendingInvoices[0].issued_at && (
                  <div className="sub-bill-due">Due on {new Date(pendingInvoices[0].issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                )}
              </div>
              <div className="sub-bill-vdivider" />
              <div className="sub-bill-col">
                <div className="sub-bill-label">Current plan</div>
                <div className="sub-bill-plan">
                  <strong>{current?.plan?.name || "—"}</strong>
                  {current?.status && (
                    <span className={`sub-bill-badge ${current.status === "active" || current.status === "trial" ? "ok" : "warn"}`}>
                      {current.status === "trial" ? "Trial" : titleize(current.status)}
                    </span>
                  )}
                </div>
                <div className="sub-bill-plan-detail">
                  {(() => { const p = plans.find(x => x.code === currentCode); return p?.pricing?.monthly?.amount ? `₹${Number(p.pricing.monthly.amount).toLocaleString("en-IN")} / month` : ""; })()}
                  {current?.billing_cycle ? ` · ${titleize(current.billing_cycle)}` : ""}
                </div>
                <div className="sub-bill-via">via Razorpay</div>
              </div>
              <div className="sub-bill-vdivider" />
              <div className="sub-bill-col">
                <div className="sub-bill-label">Next renewal</div>
                <div className="sub-bill-renewal">
                  {(() => { const d = current?.next_billing_date; if (!d) return "—"; const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); })()}
                </div>
              </div>
              <div className="sub-bill-pay-col">
                {pendingInvoices.length > 0 ? (
                  <button type="button" className="sub-bill-pay-btn" disabled={busy} onClick={() => payInvoice(pendingInvoices[0].invoice_id)}>
                    Pay now <ArrowRight size={16} />
                  </button>
                ) : (
                  <button type="button" className="sub-bill-pay-btn" disabled={busy} onClick={handleRenew}>
                    Renew <ArrowRight size={16} />
                  </button>
                )}
                <div className="sub-bill-secure"><Lock size={12} /> Secure payment<br />via Razorpay</div>
              </div>
            </div>
          </section>

          {/* Invoices */}
          <section className="sub-card">
            <div className="sub-bill-sec-head">
              <h2 className="sub-section-title"><FileText size={18} /> Invoices</h2>
            </div>
            {invoices.length === 0 ? (
              <div className="sub-empty">No invoices yet. Renew or upgrade to generate your first invoice.</div>
            ) : (
              <div className="sub-table-wrap">
                <table className="sub-table">
                  <thead>
                    <tr><th>Invoice</th><th>Plan</th><th>Amount (incl. GST)</th><th>Status</th><th>Issued</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.invoice_id}>
                        <td className="mono">{inv.invoice_number}</td>
                        <td>
                          <strong>{titleize(inv.plan_code)} Plan</strong>
                          <br />
                          <span className="sub-bill-cycle">{titleize(inv.billing_cycle)}</span>
                        </td>
                        <td><strong>₹{Number(inv.total).toLocaleString("en-IN")}</strong></td>
                        <td><span className={`sub-inv-status s-${inv.status}`}>{inv.status}</span></td>
                        <td>{inv.issued_at ? new Date(inv.issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                        <td>
                          {inv.status === "pending" && Number(inv.total) > 0 ? (
                            <button type="button" className="sub-bill-inv-pay" disabled={busy} onClick={() => payInvoice(inv.invoice_id)}>Pay now</button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="sub-bill-helper">
              <div className="sub-bill-helper-left">
                <span className="sub-bill-helper-icon"><Receipt size={16} /></span>
                <div>
                  <div className="sub-bill-helper-title">Need a receipt or invoice?</div>
                  <div className="sub-bill-helper-desc">You can download your invoice after successful payment.</div>
                </div>
              </div>
              <span className="sub-bill-helper-action"><Download size={14} /> Download invoice</span>
            </div>
          </section>

          {/* Payment History */}
          <section className="sub-card">
            <div className="sub-bill-sec-head">
              <h2 className="sub-section-title"><Receipt size={18} /> Payment history</h2>
            </div>
            {payments.length === 0 ? (
              <div className="sub-bill-empty">
                <div className="sub-bill-empty-icon"><Wallet size={28} /><span className="sub-bill-empty-rupee">₹</span></div>
                <strong>No payment history yet</strong>
                <span>Your completed payments will appear here.</span>
              </div>
            ) : (
              <div className="sub-table-wrap">
                <table className="sub-table">
                  <thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th><th>Status</th></tr></thead>
                  <tbody>
                    {payments.map((p) => (
                      <tr key={p.payment_id}>
                        <td>{p.paid_at ? new Date(p.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                        <td><strong>₹{Number(p.amount).toLocaleString("en-IN")}</strong></td>
                        <td className="cap">{p.method}</td>
                        <td className="mono">{p.reference || "—"}</td>
                        <td><span className="sub-inv-status s-paid">{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Billing Requests + Help */}
          <div className="sub-bill-bottom">
            <section className="sub-card sub-bill-req-section">
              <h2 className="sub-section-title"><MessageSquare size={18} /> Billing requests</h2>
              <p className="sub-bill-req-sub">Need help with your subscription or billing?</p>
              <div className="sub-bill-req-grid">
                <button type="button" className="sub-bill-req-opt" onClick={() => openRequest("upgrade", "")}>
                  <span className="sub-bill-req-ic"><Crown size={16} /></span>
                  <div>
                    <div className="sub-bill-req-title">Change plan</div>
                    <div className="sub-bill-req-desc">Upgrade, downgrade or switch plans</div>
                  </div>
                  <ChevronRight size={16} className="sub-bill-req-arrow" />
                </button>
                <button type="button" className="sub-bill-req-opt" onClick={() => openRequest("contact_sales", "")}>
                  <span className="sub-bill-req-ic"><FileText size={16} /></span>
                  <div>
                    <div className="sub-bill-req-title">Custom pricing</div>
                    <div className="sub-bill-req-desc">Get a custom quote for your organization</div>
                  </div>
                  <ChevronRight size={16} className="sub-bill-req-arrow" />
                </button>
                <button type="button" className="sub-bill-req-opt" onClick={() => openRequest("other", "")}>
                  <span className="sub-bill-req-ic"><HelpCircle size={16} /></span>
                  <div>
                    <div className="sub-bill-req-title">Billing support</div>
                    <div className="sub-bill-req-desc">We're here to help with any billing issues</div>
                  </div>
                  <ChevronRight size={16} className="sub-bill-req-arrow" />
                </button>
                <button type="button" className="sub-bill-req-opt" onClick={() => openRequest("other", "")}>
                  <span className="sub-bill-req-ic"><Headphones size={16} /></span>
                  <div>
                    <div className="sub-bill-req-title">General support</div>
                    <div className="sub-bill-req-desc">General questions about billing or invoices</div>
                  </div>
                  <ChevronRight size={16} className="sub-bill-req-arrow" />
                </button>
              </div>
              <button type="button" className="sub-bill-req-send" onClick={() => openRequest("other", "")}>
                <Send size={14} /> Send a billing request
              </button>
              {reqs.length > 0 && (
                <div className="sub-table-wrap" style={{ marginTop: '1rem' }}>
                  <table className="sub-table">
                    <thead><tr><th>Type</th><th>Target</th><th>Status</th><th>Raised</th></tr></thead>
                    <tbody>
                      {reqs.map((r) => (
                        <tr key={r.request_id}>
                          <td className="cap">{r.request_type?.replace(/_/g, " ")}</td>
                          <td>{r.target_plan || "—"}</td>
                          <td><span className={`sub-inv-status s-${r.status === "approved" || r.status === "done" ? "paid" : "pending"}`}>{r.status}</span></td>
                          <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <div className="sub-bill-help-card">
              <h3 className="sub-bill-help-title">Need help paying?</h3>
              <p className="sub-bill-help-text">If you're facing any issues or have questions, our support team is ready to help.</p>
              <div className="sub-bill-help-illus"><Headphones size={36} /></div>
              <button type="button" className="sub-bill-help-btn" onClick={() => openRequest("other", "")}>
                <Headphones size={14} /> Contact support
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="sub-bill-footer">
            <span className="sub-bill-footer-item"><CheckCircle2 size={14} /> All payments are processed securely via Razorpay.</span>
            <span className="sub-bill-footer-brand">Razorpay</span>
          </div>
        </>
      ) : null}

      {/* ── ADD-ONS ── */}
      {tab === "addons" ? (
        <>
          {myAddons.length > 0 ? (
            <div className="sub-ao-active-bar">
              <span className="sub-ao-active-label">Your active add-ons</span>
              <div className="sub-ao-active-chips">
                {myAddons.map((a) => <span className="sub-chip" key={a.id}>{a.addon_code}{a.quantity > 1 ? ` ×${a.quantity}` : ""}</span>)}
              </div>
            </div>
          ) : null}

          <section className="sub-ao-intro">
            <div className="sub-ao-intro-left">
              <h2 className="sub-ao-heading">Add-ons</h2>
              <p className="sub-ao-subtext">Extend your BrixiGo experience with powerful add-ons that grow with your business.</p>
            </div>
            <div className="sub-ao-trust-row">
              <div className="sub-ao-trust-item">
                <span className="sub-ao-trust-icon" style={{ background: '#ecfdf5', color: '#16a34a' }}><ShieldCheck size={18} /></span>
                <div>
                  <div className="sub-ao-trust-label">Secure</div>
                  <div className="sub-ao-trust-desc">Your data stays safe and protected.</div>
                </div>
              </div>
              <div className="sub-ao-trust-item">
                <span className="sub-ao-trust-icon" style={{ background: '#eff6ff', color: '#2563eb' }}><Zap size={18} /></span>
                <div>
                  <div className="sub-ao-trust-label">Flexible</div>
                  <div className="sub-ao-trust-desc">Add or remove add-ons anytime you need.</div>
                </div>
              </div>
              <div className="sub-ao-trust-item">
                <span className="sub-ao-trust-icon" style={{ background: '#f3e8ff', color: '#7c3aed' }}><Headphones size={18} /></span>
                <div>
                  <div className="sub-ao-trust-label">Supported</div>
                  <div className="sub-ao-trust-desc">Our team is here to help you 24/7.</div>
                </div>
              </div>
            </div>
          </section>

          <h3 className="sub-ao-pop-title">Popular add-ons</h3>

          <div className="sub-ao-grid">
            <article className="sub-ao-card">
              <div className="sub-ao-card-header">
                <span className="sub-ao-card-icon" style={{ background: '#eef2ff', color: '#4f46e5' }}><Code2 size={22} /></span>
                <div className="sub-ao-card-price">
                  <span className="sub-ao-amt">₹{addonPrice("ADDON_API", 999).toLocaleString("en-IN")}</span>
                  <span className="sub-ao-per">/ month</span>
                </div>
              </div>
              <h4 className="sub-ao-card-name">API Access</h4>
              <p className="sub-ao-card-desc">Connect BrixiGo with your other tools and systems.</p>
              <ul className="sub-ao-card-benefits">
                <li><Check size={14} className="sub-ao-check" /> RESTful API access</li>
                <li><Check size={14} className="sub-ao-check" /> Developer friendly</li>
                <li><Check size={14} className="sub-ao-check" /> Integrate third-party apps</li>
              </ul>
              <button type="button" className="sub-ao-cta purple" onClick={() => openRequest("other", "")}>
                <Send size={14} /> Request API Access
              </button>
            </article>

            <article className="sub-ao-card">
              <div className="sub-ao-card-header">
                <span className="sub-ao-card-icon" style={{ background: '#fff7ed', color: '#ea580c' }}><Users size={22} /></span>
                <div className="sub-ao-card-price">
                  <span className="sub-ao-price-from">Starts from</span>
                  <span className="sub-ao-amt">₹{addonPrice("EMP_PACK_50", 499).toLocaleString("en-IN")}</span>
                  <span className="sub-ao-per">/ month</span>
                </div>
              </div>
              <h4 className="sub-ao-card-name">Increase Employee Limit</h4>
              <p className="sub-ao-card-desc">Need more employee capacity? Choose the option that suits your organization.</p>
              <div className="sub-ao-emp-options">
                <label className={`sub-ao-emp-opt${selectedEmpPack === "EMP_PACK_50" ? " active" : ""}`}>
                  <input type="radio" name="empPack" value="EMP_PACK_50" checked={selectedEmpPack === "EMP_PACK_50"} onChange={(e) => setSelectedEmpPack(e.target.value)} />
                  <span className="sub-ao-emp-opt-radio" />
                  <span className="sub-ao-emp-opt-label">+50 Employees</span>
                  <span className="sub-ao-emp-opt-price">₹{addonPrice("EMP_PACK_50", 499).toLocaleString("en-IN")} / month</span>
                </label>
                <label className={`sub-ao-emp-opt${selectedEmpPack === "EMP_PACK_200" ? " active" : ""}`}>
                  <input type="radio" name="empPack" value="EMP_PACK_200" checked={selectedEmpPack === "EMP_PACK_200"} onChange={(e) => setSelectedEmpPack(e.target.value)} />
                  <span className="sub-ao-emp-opt-radio" />
                  <span className="sub-ao-emp-opt-label">+200 Employees</span>
                  <span className="sub-ao-emp-opt-price">₹{addonPrice("EMP_PACK_200", 1799).toLocaleString("en-IN")} / month</span>
                </label>
              </div>
              <button type="button" className="sub-ao-cta orange" onClick={() => openRequest("other", "")}>
                <ShoppingCart size={14} /> Choose Capacity
              </button>
            </article>

            <article className="sub-ao-card">
              <div className="sub-ao-card-header">
                <span className="sub-ao-card-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}><Cloud size={22} /></span>
                <div className="sub-ao-card-price">
                  <span className="sub-ao-amt">₹{addonPrice("STORAGE_50", 399).toLocaleString("en-IN")}</span>
                  <span className="sub-ao-per">/ month</span>
                </div>
              </div>
              <h4 className="sub-ao-card-name">Extra Storage</h4>
              <p className="sub-ao-card-desc">Get more space to store your documents and important data.</p>
              <ul className="sub-ao-card-benefits">
                <li><Check size={14} className="sub-ao-check" /> Add 50 GB storage</li>
                <li><Check size={14} className="sub-ao-check" /> Secure & reliable</li>
                <li><Check size={14} className="sub-ao-check" /> Always available</li>
              </ul>
              <button type="button" className="sub-ao-cta green" onClick={() => openRequest("other", "")}>
                <HardDrive size={14} /> Add Storage
              </button>
            </article>

            <article className="sub-ao-card">
              <div className="sub-ao-card-header">
                <span className="sub-ao-card-icon" style={{ background: '#f3e8ff', color: '#7c3aed' }}><Sparkles size={22} /></span>
              </div>
              <h4 className="sub-ao-card-name">Custom Solution</h4>
              <p className="sub-ao-card-desc">Looking for something unique? We can build a solution that fits your business needs.</p>
              <ul className="sub-ao-card-benefits">
                <li><Check size={14} className="sub-ao-check" /> Tailored to your requirements</li>
                <li><Check size={14} className="sub-ao-check" /> Scalable as you grow</li>
                <li><Check size={14} className="sub-ao-check" /> Expert consultation</li>
              </ul>
              <button type="button" className="sub-ao-cta gray" onClick={() => openRequest("contact_sales", "")}>
                <Headphones size={14} /> Contact Sales
              </button>
            </article>
          </div>

          {otherAddons.length > 0 ? (
            <div className="sub-ao-grid" style={{ marginTop: '0.75rem' }}>
              {otherAddons.map((a) => (
                <article className="sub-ao-card" key={a.code}>
                  <div className="sub-ao-card-header">
                    <span className="sub-ao-card-icon" style={{ background: '#f1f5f9', color: '#475569' }}><Package size={22} /></span>
                    <div className="sub-ao-card-price">
                      <span className="sub-ao-amt">{Number(a.price) ? `₹${Number(a.price).toLocaleString("en-IN")}` : "Free"}</span>
                      {Number(a.price) ? <span className="sub-ao-per">/ month</span> : null}
                    </div>
                  </div>
                  <h4 className="sub-ao-card-name">{a.name}</h4>
                  <p className="sub-ao-card-desc">{a.description}</p>
                  <button type="button" className="sub-ao-cta purple" onClick={() => openRequest("other", "")}>
                    <Send size={14} /> Request
                  </button>
                </article>
              ))}
            </div>
          ) : null}

          <section className="sub-ao-how">
            <div className="sub-ao-how-title"><Info size={16} /> How add-ons work</div>
            <div className="sub-ao-steps">
              <div className="sub-ao-step">
                <span className="sub-ao-step-icon" style={{ background: '#ecfdf5', color: '#16a34a' }}><Package size={20} /></span>
                <strong>1. Choose</strong>
                <span>Select the add-on that fits your needs.</span>
              </div>
              <ChevronRight size={18} className="sub-ao-step-arrow" />
              <div className="sub-ao-step">
                <span className="sub-ao-step-icon" style={{ background: '#eff6ff', color: '#2563eb' }}><FileText size={20} /></span>
                <strong>2. Request</strong>
                <span>Submit a request and our team will review it.</span>
              </div>
              <ChevronRight size={18} className="sub-ao-step-arrow" />
              <div className="sub-ao-step">
                <span className="sub-ao-step-icon" style={{ background: '#ecfdf5', color: '#16a34a' }}><CheckCircle2 size={20} /></span>
                <strong>3. Activate</strong>
                <span>We'll activate the add-on quickly for you.</span>
              </div>
              <ChevronRight size={18} className="sub-ao-step-arrow" />
              <div className="sub-ao-step">
                <span className="sub-ao-step-icon" style={{ background: '#f3e8ff', color: '#7c3aed' }}><TrendingUp size={20} /></span>
                <strong>4. Use & Grow</strong>
                <span>Enjoy the new capability and grow your business.</span>
              </div>
            </div>
          </section>

          <section className="sub-ao-help">
            <div className="sub-ao-help-content">
              <span className="sub-ao-help-icon"><Headphones size={22} /></span>
              <div>
                <div className="sub-ao-help-title">Need help choosing the right add-on?</div>
                <div className="sub-ao-help-text">Our experts can help you find the perfect solution for your business.</div>
              </div>
            </div>
            <button type="button" className="sub-ao-help-btn" onClick={() => openRequest("contact_sales", "")}>
              <Phone size={14} /> Talk to an Expert
            </button>
          </section>

          <div className="sub-ao-footer">
            <span className="sub-ao-footer-item"><CheckCircle2 size={14} /> All add-ons are billed monthly. Cancel or change anytime.</span>
            <span className="sub-ao-footer-item"><Lock size={14} /> All payments are processed securely via Razorpay.</span>
          </div>
        </>
      ) : null}

      {featModalOpen ? (
        <div className="sub-ov-fm-overlay" role="dialog" aria-modal="true" onClick={() => setFeatModalOpen(false)}>
          <div className="sub-ov-fm-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="sub-ov-fm-close" onClick={() => setFeatModalOpen(false)}><X size={18} /></button>

            <div className="sub-ov-fm-header">
              <div className="sub-ov-fm-header-icon"><Crown size={22} /></div>
              <div>
                <h2 className="sub-ov-fm-title">{current?.plan?.name || "Your"} Plan</h2>
                <p className="sub-ov-fm-subtitle">Everything included in your current subscription.</p>
              </div>
            </div>

            <div className="sub-ov-fm-body">
              <div className="sub-ov-fm-section">
                <h3 className="sub-ov-fm-sec-title">
                  <CheckCircle2 size={16} className="sub-ov-fm-icon-green" /> Included in your plan
                  <span className="sub-ov-fm-count">{includedFeats.length}</span>
                </h3>
                <div className="sub-ov-fm-grid">
                  {includedFeats.map((row) => {
                    const meta = OV_FEATURE_META[row.key] || {};
                    const FIcon = meta.icon || Sparkles;
                    let valueText = meta.desc || row.label;
                    if (row.fn) {
                      const val = row.fn(currentPlan || {});
                      if (typeof val === "string" || typeof val === "number") valueText = String(val);
                    }
                    return (
                      <div className="sub-ov-fm-item" key={row.key}>
                        <div className="sub-ov-fm-item-icon included"><FIcon size={16} /></div>
                        <div>
                          <div className="sub-ov-fm-item-name">{row.label}</div>
                          <div className="sub-ov-fm-item-desc">{valueText}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {addonCatalog.length > 0 ? (
                <div className="sub-ov-fm-section">
                  <h3 className="sub-ov-fm-sec-title">
                    <ShoppingCart size={16} className="sub-ov-fm-icon-orange" /> Available as Add-ons
                    <span className="sub-ov-fm-count">{addonCatalog.length}</span>
                  </h3>
                  <div className="sub-ov-fm-addon-list">
                    {addonCatalog.map((addon) => {
                      const display = OV_ADDON_DISPLAY[addon.code] || {};
                      const AIcon = display.icon || Package;
                      return (
                        <div className="sub-ov-fm-addon" key={addon.code}>
                          <div className="sub-ov-fm-addon-icon"><AIcon size={16} /></div>
                          <div className="sub-ov-fm-addon-body">
                            <div className="sub-ov-fm-addon-name">{addon.name || titleize(addon.code)}</div>
                            <div className="sub-ov-fm-addon-desc">{display.desc || addon.description || ""}</div>
                          </div>
                          <button type="button" className="sub-ov-fm-addon-cta" onClick={() => { setFeatModalOpen(false); setTab("addons"); }}>
                            Explore <ArrowRight size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {upgradeFeats.length > 0 ? (
                <div className="sub-ov-fm-section">
                  <h3 className="sub-ov-fm-sec-title">
                    <Lock size={16} className="sub-ov-fm-icon-gray" /> Available with another plan
                    <span className="sub-ov-fm-count">{upgradeFeats.length}</span>
                  </h3>
                  <div className="sub-ov-fm-grid">
                    {upgradeFeats.map((row) => {
                      const meta = OV_FEATURE_META[row.key] || {};
                      const FIcon = meta.icon || Sparkles;
                      return (
                        <div className="sub-ov-fm-item sub-ov-fm-item-locked" key={row.key}>
                          <div className="sub-ov-fm-item-icon locked"><FIcon size={16} /></div>
                          <div>
                            <div className="sub-ov-fm-item-name">{row.label}</div>
                            <div className="sub-ov-fm-item-desc">Available in higher plans</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button type="button" className="sub-ov-fm-upgrade-cta" onClick={() => { setFeatModalOpen(false); setTab("plans"); }}>
                    Compare plans &amp; upgrade <ArrowRight size={14} />
                  </button>
                </div>
              ) : null}
            </div>

            <div className="sub-ov-fm-footer">
              <ShieldCheck size={14} /> Secure &amp; trusted <span className="sub-ov-fm-footer-sep">|</span> Powered by <span className="sub-ov-footer-brand">Razorpay</span>
            </div>
          </div>
        </div>
      ) : null}

      {reqOpen ? (
        <div className="sub-modal-overlay" role="dialog" aria-modal="true" onClick={() => !busy && setReqOpen(false)}>
          <div className="sub-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sub-modal-badge"><Send size={24} /></div>
            <h3 className="sub-modal-title">Billing request</h3>
            <div className="sub-req-form">
              <label>Type
                <select value={reqForm.request_type} onChange={(e) => setReqForm({ ...reqForm, request_type: e.target.value })}>
                  <option value="upgrade">Upgrade</option>
                  <option value="downgrade">Downgrade</option>
                  <option value="cancel">Cancel</option>
                  <option value="contact_sales">Contact sales</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>Target plan
                <select value={reqForm.target_plan} onChange={(e) => setReqForm({ ...reqForm, target_plan: e.target.value })}>
                  <option value="">—</option>
                  {plans.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                </select>
              </label>
              <label>Message
                <textarea rows={3} value={reqForm.message} onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })} />
              </label>
            </div>
            <div className="sub-modal-actions">
              <button type="button" className="sub-btn sub-btn-ghost" onClick={() => setReqOpen(false)} disabled={busy}>Cancel</button>
              <button type="button" className="sub-btn sub-btn-primary" onClick={submitRequest} disabled={busy}>
                <Clock size={14} /> {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
